// Auswertung EINES Arbeitstags eines Mitarbeiters — die einzige Stelle, an der Ist, Pause, Soll und
// Differenz berechnet werden (Tagesansicht im Browser, Webhook, Tagesabschluss, Monatsbericht).
//
// Kein Kommen/Gehen am Leser: Ein UniFi-Leser liefert keine Richtung. Jede zählende Stempelung
// wechselt den Zustand — die 1. des Tages ist Kommen, die 2. Gehen, die 3. wieder Kommen …
// Arbeitsblöcke sind diese Paare, Pausen die Lücken dazwischen.

import type { TimeRules } from "./rules.ts";
import type { LocalDate } from "./time.ts";

export type PunchStatus = "valid" | "duplicate" | "rejected_foreign_terminal" | "voided";

export type AbsenceType =
  | "ferie"
  | "permessi"
  | "rol"
  | "sick"
  | "injury"
  | "parental"
  | "training"
  | "other";

export interface DayPunch {
  id: string;
  occurredAt: string;
  status: PunchStatus;
}

export interface DayAbsence {
  type: AbsenceType;
  /** null = ganzer Tag ⇒ das Tages-Soll wird gutgeschrieben. */
  minutesPerDay: number | null;
}

export interface DayInput {
  punches: readonly DayPunch[];
  rules: TimeRules;
  /** Soll laut Zeitplan-Vorlage, VOR Feiertag und Abwesenheit. */
  scheduledMinutes: number;
  holidayName?: string | null;
  absence?: DayAbsence | null;
  /** Pausen-Nachtrag (FR-10): genommene, aber nicht gestempelte Pause. Hat Vorrang vor FR-07. */
  reportedBreakMinutes?: number | null;
  /** Tagesabschluss (FR-07): fehlende Pflichtpause abziehen, offene Sitzung als Anomalie. */
  closing: boolean;
  /**
   * Nur für Live-Anzeigen (heute): Eine offene Sitzung zählt bis zu diesem Zeitpunkt.
   * Gespeicherte Auswertungen lassen das weg — es wird nichts erfunden.
   */
  countOpenUntil?: string;
}

export type AnomalyCode =
  | "missing_clock_out"
  | "no_presence"
  | "foreign_terminal"
  | "break_shortfall"
  | "break_report_short"
  | "auto_break_applied"
  | "duplicate_ignored"
  | "work_on_day_off";

export interface Anomaly {
  code: AnomalyCode;
  /** warning = braucht Klärung vor dem Monatsabschluss; info = nur Hinweis. */
  severity: "warning" | "info";
  at?: string;
  minutes?: number;
}

export interface Interval {
  start: string;
  end: string | null;
}

export interface DayResult {
  intervals: Interval[];
  /** Zählende Stempelungen nach Entfernen der Doppelten, chronologisch. */
  countedPunchIds: string[];
  /** Stempelungen, die als doppelt erkannt wurden (Status beim Speichern nachziehen). */
  duplicatePunchIds: string[];
  present: boolean;
  /** Anwesenheit laut Stempelungen, vor jedem Pausenabzug. */
  grossMinutes: number;
  /** Summe der Lücken, die lang genug für eine Pause sind (minBreakSegmentMinutes). */
  stampedBreakMinutes: number;
  reportedBreakMinutes: number;
  breakRequired: boolean;
  breakShortfallMinutes: number;
  autoBreakMinutes: number;
  /** Arbeitszeit nach Nachtrag und automatischer Pause — das zählt fürs Konto. */
  workedMinutes: number;
  targetMinutes: number;
  creditMinutes: number;
  deltaMinutes: number;
  anomalies: Anomaly[];
}

const MINUTE = 60_000;

/** Ganze Minuten, abgeschnitten — gerechnet wird vorher in Millisekunden (FR-03: keine Rundung). */
function toMinutes(ms: number): number {
  return Math.floor(ms / MINUTE);
}

/**
 * Entfernt versehentliche Doppel-Stempelungen: Liegt eine Stempelung innerhalb des Sperrfensters
 * nach der zuletzt GEZÄHLTEN, zählt sie nicht. Abgeleitet aus allen Stempelungen des Tags, nicht aus
 * dem gespeicherten Status — so stimmt das Ergebnis auch, wenn das Nachholen Stempelungen
 * nachträglich und in anderer Reihenfolge einfügt.
 */
export function collapseDuplicates(
  punches: readonly DayPunch[],
  windowSeconds: number,
): { counted: DayPunch[]; duplicates: DayPunch[] } {
  const candidates = punches
    .filter((p) => p.status === "valid" || p.status === "duplicate")
    .slice()
    .sort((a, b) => a.occurredAt.localeCompare(b.occurredAt) || a.id.localeCompare(b.id));
  const counted: DayPunch[] = [];
  const duplicates: DayPunch[] = [];
  for (const punch of candidates) {
    const last = counted[counted.length - 1];
    if (last && Date.parse(punch.occurredAt) - Date.parse(last.occurredAt) < windowSeconds * 1000) {
      duplicates.push(punch);
    } else {
      counted.push(punch);
    }
  }
  return { counted, duplicates };
}

export function evaluateDay(input: DayInput): DayResult {
  const { rules } = input;
  const anomalies: Anomaly[] = [];
  const { counted, duplicates } = collapseDuplicates(input.punches, rules.duplicateWindowSeconds);

  for (const punch of input.punches) {
    if (punch.status === "rejected_foreign_terminal") {
      anomalies.push({ code: "foreign_terminal", severity: "warning", at: punch.occurredAt });
    }
  }
  for (const punch of duplicates) {
    anomalies.push({ code: "duplicate_ignored", severity: "info", at: punch.occurredAt });
  }

  // Paare bilden.
  const intervals: Interval[] = [];
  for (let i = 0; i < counted.length; i += 2) {
    intervals.push({ start: counted[i]!.occurredAt, end: counted[i + 1]?.occurredAt ?? null });
  }
  const openInterval = intervals.length > 0 && intervals[intervals.length - 1]!.end === null;

  let grossMs = 0;
  for (const interval of intervals) {
    if (interval.end) {
      grossMs += Date.parse(interval.end) - Date.parse(interval.start);
    } else if (input.countOpenUntil && !input.closing) {
      grossMs += Math.max(0, Date.parse(input.countOpenUntil) - Date.parse(interval.start));
    }
  }
  if (openInterval && input.closing) {
    anomalies.push({
      code: "missing_clock_out",
      severity: "warning",
      at: intervals[intervals.length - 1]!.start,
    });
  }

  let stampedBreakMs = 0;
  for (let i = 1; i < intervals.length; i++) {
    const gap = Date.parse(intervals[i]!.start) - Date.parse(intervals[i - 1]!.end!);
    if (gap >= rules.minBreakSegmentMinutes * MINUTE) stampedBreakMs += gap;
  }

  const grossMinutes = toMinutes(grossMs);
  const stampedBreakMinutes = toMinutes(stampedBreakMs);
  const reported = input.reportedBreakMinutes ?? null;
  const reportedBreakMinutes = reported !== null ? Math.max(0, Math.min(reported, grossMinutes)) : 0;
  const afterReport = grossMinutes - reportedBreakMinutes;

  // FR-05: Pflichtpause bei MEHR als breakThresholdMinutes Arbeitszeit.
  const breakRequired = rules.requiredBreakMinutes > 0 && afterReport > rules.breakThresholdMinutes;
  const takenBreak = stampedBreakMinutes + reportedBreakMinutes;
  const breakShortfallMinutes = breakRequired
    ? Math.max(0, rules.requiredBreakMinutes - takenBreak)
    : 0;

  let autoBreakMinutes = 0;
  if (breakShortfallMinutes > 0) {
    if (reported !== null) {
      // Gemeldete kürzere Pause (FR-10): kein automatischer Abzug, aber zur Kenntnis.
      anomalies.push({ code: "break_report_short", severity: "warning", minutes: breakShortfallMinutes });
    } else if (input.closing && rules.autoBreakDeduction) {
      autoBreakMinutes = rules.capAutoBreakAtThreshold
        ? Math.min(breakShortfallMinutes, afterReport - rules.breakThresholdMinutes)
        : breakShortfallMinutes;
      anomalies.push({ code: "auto_break_applied", severity: "info", minutes: autoBreakMinutes });
    } else if (input.closing) {
      anomalies.push({ code: "break_shortfall", severity: "warning", minutes: breakShortfallMinutes });
    }
  }

  const workedMinutes = afterReport - autoBreakMinutes;

  // Soll und Gutschrift.
  let targetMinutes = Math.max(0, input.scheduledMinutes);
  if (input.holidayName) targetMinutes = 0;
  let creditMinutes = 0;
  if (input.absence && targetMinutes > 0) {
    creditMinutes =
      input.absence.minutesPerDay === null
        ? targetMinutes
        : Math.min(input.absence.minutesPerDay, targetMinutes);
  }

  const hasPresence = counted.length > 0;
  if (input.closing && !hasPresence && targetMinutes > 0 && creditMinutes < targetMinutes) {
    anomalies.push({ code: "no_presence", severity: "warning" });
  }
  if (hasPresence && (targetMinutes === 0 || (input.absence && input.absence.minutesPerDay === null))) {
    anomalies.push({ code: "work_on_day_off", severity: "info" });
  }

  return {
    intervals,
    countedPunchIds: counted.map((p) => p.id),
    duplicatePunchIds: duplicates.map((p) => p.id),
    present: openInterval,
    grossMinutes,
    stampedBreakMinutes,
    reportedBreakMinutes,
    breakRequired,
    breakShortfallMinutes,
    autoBreakMinutes,
    workedMinutes,
    targetMinutes,
    creditMinutes,
    deltaMinutes: workedMinutes + creditMinutes - targetMinutes,
    anomalies,
  };
}

/**
 * FR-06: Braucht dieser Mitarbeiter jetzt (zur Erinnerungszeit) einen Hinweis auf die Mittagspause?
 * Ja, wenn er gerade eingestempelt ist, noch keine ausreichende Pause hat und die Schwelle heute
 * voraussichtlich überschreitet (schon jetzt darüber, oder das Tages-Soll liegt darüber).
 */
export function needsBreakReminder(
  punches: readonly DayPunch[],
  rules: TimeRules,
  scheduledMinutes: number,
  now: string,
): boolean {
  if (rules.requiredBreakMinutes <= 0) return false;
  const day = evaluateDay({ punches, rules, scheduledMinutes, closing: false, countOpenUntil: now });
  if (!day.present) return false;
  if (day.stampedBreakMinutes >= rules.requiredBreakMinutes) return false;
  return day.grossMinutes > rules.breakThresholdMinutes || scheduledMinutes > rules.breakThresholdMinutes;
}

/** Soll laut Vorlage für einen Wochentag (0 = Montag). */
export function scheduledMinutesFor(weekdayMinutes: readonly number[] | null | undefined, weekday: number): number {
  return weekdayMinutes?.[weekday] ?? 0;
}

export type { LocalDate };
